import { useRef } from 'react';
import { useCloudinaryResize } from '@socialchorus/shared-ui-components';
import { BannerHomepageModule } from '../../models/home-page-custom/types';
import { useLinkClickHandler } from '../../hooks/useLinkClickHandler';

type BannerSize = 'ExtraSmall' | 'Small' | 'Medium' | 'Large';

const getSizeClass = (size: BannerSize | undefined): string => {
  switch (size) {
    case 'ExtraSmall':
      return 'home__banner--extra-small';
    case 'Small':
      return 'home__banner--small';
    case 'Medium':
      return 'home__banner--medium';
    case 'Large':
      return 'home__banner--large';
    default:
      return 'home__banner--medium'; // Default to medium
  }
};

export const HomepageBanner = ({
  bannerModule,
}: {
  bannerModule: BannerHomepageModule;
}) => {
  const settings = bannerModule?.attributes.settings;
  const sizeClass = getSizeClass(settings?.size);
  const imageUrl = settings?.image_url;
  const webImage = settings?.web_image;

  const { handleClick, handleKeyDown, normalizedUrl } =
    useLinkClickHandler(imageUrl);

  const imageRef = useRef<HTMLImageElement>(null);
  const resizedSrc = useCloudinaryResize({
    imageRef,
    originalImgSrc: webImage,
    aspectRatio: settings?.aspect_ratio,
  });

  const bannerContent = (
    <img
      ref={imageRef}
      src={resizedSrc ?? webImage}
      alt={settings?.aria_label}
    />
  );

  // If there's an image_url, render as a link
  if (normalizedUrl) {
    return (
      <a
        href={normalizedUrl}
        className={`home__banner ${sizeClass}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {bannerContent}
      </a>
    );
  }

  // Otherwise, render as a div
  return <div className={`home__banner ${sizeClass}`}>{bannerContent}</div>;
};
